'use client';

import { Box, HStack, Icon, IconButton, Stack, useBreakpointValue, } from "@chakra-ui/react";
import { useKeenSlider } from "keen-slider/react";
import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Skeleton } from "@/components/ui/skeleton";
import { PublicMainSlide } from "@/types/slides";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import Link from "next/link";
import { getUploadsUrl } from "@/utils/file";

const MainSlider = ({ slides }: { slides: PublicMainSlide[] }) => {
  const [ loaded, setLoaded ] = useState(false);
  const [ firstMediaLoaded, setFirstMediaLoaded ] = useState(false);
  const [ currentSlide, setCurrentSlide ] = useState(0);

  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  const slidesPerView = useBreakpointValue({
    base: 1,
    md: 1,
  });

  const playActive = useCallback((index: number) => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;

      if (i === index) {
        video.play().catch(() => {
        });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, []);

  const [ sliderRef, instanceRef ] = useKeenSlider<HTMLDivElement>({
    loop: true,
    renderMode: "precision",
    slides: {
      perView: slidesPerView ?? 1,
      spacing: 20,
      origin: "auto",
    },
    slideChanged(slider) {
      const rel = slider.track.details.rel;
      setCurrentSlide(rel);
      playActive(rel);
    },
    created(slider) {
      setLoaded(true);
      playActive(slider.track.details.rel);
    },
  });

  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        videoRefs.current.forEach(v => v?.pause());
      } else {
        const activeIndex = instanceRef.current?.track.details.rel;
        if (typeof activeIndex === "number") {
          playActive(activeIndex);
        }
      }
    };

    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [ playActive, instanceRef ]);

  useEffect(() => {
    if (!instanceRef.current || !slides.length) return;
    if (!slides || slides.length < 2) return;
    const current = slides[currentSlide];
    const duration = (current?.duration ?? 10) * 1000;
    const timeout = setTimeout(() => {
      instanceRef.current?.next();
    }, duration);
    return () => clearTimeout(timeout);
  }, [currentSlide, slides, instanceRef]);
  
  const ready = loaded && firstMediaLoaded && slides.length > 0;

  return (
    <Stack gap="40px">
      <Box
        aspectRatio={{ base: "9 / 16", md: "16 / 9" }}
        position="relative"
        w="100%"
        overflow="hidden"
      >
        {!ready && (
          <Box position="absolute" inset={0} zIndex={1}>
            <Skeleton w="100%" h="100%" />
          </Box>
        )}

        {loaded && slides.length > 1 && instanceRef.current && (
          <HStack
            position="absolute"
            top="50%"
            left={2}
            right={2}
            justify="space-between"
            zIndex={2}
            pointerEvents="none"
          >
            <IconButton
              aria-label="Prev"
              onClick={() => instanceRef.current?.prev()}
              variant="plain"
              pointerEvents="all"
            >
              <Icon as={MdChevronLeft} boxSize={10} color="white" />
            </IconButton>

            <IconButton
              aria-label="Next"
              onClick={() => instanceRef.current?.next()}
              variant="plain"
              pointerEvents="all"
            >
              <Icon as={MdChevronRight} boxSize={10} color="white" />
            </IconButton>
          </HStack>
        )}

        <div
          ref={sliderRef}
          className="keen-slider"
          style={{ opacity: ready ? 1 : 0, height: "100%" }}
        >
          {slides.length ? (
            slides.map((slide, index) => {
              const isFirst = index === 0;
              const isActive = index === currentSlide;

              return (
                <Box
                  key={slide.order}
                  className={`keen-slider__slide ${slide.slideType === 'IMAGE' ? 'main-slider__slide-image' : ''}`}
                  position="relative"
                  aspectRatio={{ base: "9 / 16", md: "16 / 9" }}
                  w="100%"
                >
                  {slide.slideType === "IMAGE" ? (
                    <>
                      {slide.images.main?.path && (
                        <Image
                          src={`${getUploadsUrl()}/${slide.images.main.path}`}
                          alt={`Slide ${slide.order}`}
                          fill
                          sizes="(max-width: 768px) 100vw, 100vw"
                          quality={40}
                          priority={isFirst}
                          loading={isFirst ? "eager" : "lazy"}
                          onLoad={() => isFirst && setFirstMediaLoaded(true)}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'top',
                            filter: 'brightness(80%)',
                          }}
                          className={'slide-img slide-img--desktop'}
                        />
                      )}

                      {slide.images.mobile?.path && (
                        <Image
                          src={`${getUploadsUrl()}/${slide.images.mobile.path}`}
                          alt={`Slide ${slide.order} mobile`}
                          fill
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'top',
                            filter: 'brightness(80%)',
                          }}
                          sizes="(max-width: 768px) 100vw, 100vw"
                          quality={40}
                          priority={false}
                          loading="lazy"
                          className={'slide-img slide-img--mobile'}
                        />
                      )}
                    </>
                  ) : (
                    <video
                      ref={(el) => {
                        videoRefs.current[index] = el ?? null;
                      }} muted
                      loop
                      playsInline
                      preload={isActive ? "metadata" : "none"}
                      onLoadedData={() =>
                        isFirst && setFirstMediaLoaded(true)
                      }
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "brightness(80%)",
                      }}
                    >
                      {slide.videos.main?.path && (
                        <source
                          media="all and (min-width: 768px)"
                            src={`${getUploadsUrl()}${slide.videos.main.path}`}
                          type="video/mp4"
                        />
                      )}

                      {slide.videos.mobile?.path && (
                        <source
                          media="all and (max-width: 767px)"
                            src={`${getUploadsUrl()}${slide.videos.mobile.path}`}
                          type="video/mp4"
                        />
                      )}
                    </video>
                  )}

                  {slide.link && (
                    <Link
                      href={slide.link}
                      aria-label="Open slide link"
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                      }}
                    />
                  )}
                </Box>
              );
            })
          ) : (
            <Box className="keen-slider__slide">Нет слайдов</Box>
          )}
        </div>
      </Box>
    </Stack>
  );
};

export default MainSlider;
